import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Mail, CheckCircle, XCircle, Search, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABELS = {
  welcome: 'Bienvenida',
  package_received: 'Paquete Recibido',
  status_change: 'Cambio de Estado',
  warehouse_receipt: 'WR Creado',
  invoice: 'Factura',
  weekly_summary: 'Resumen Semanal',
  payment_verified: 'Pago Aprobado',
  payment_rejected: 'Pago Rechazado',
  general: 'General',
};

const TYPE_COLORS = {
  welcome: 'bg-blue-100 text-blue-700',
  package_received: 'bg-green-100 text-green-700',
  status_change: 'bg-purple-100 text-purple-700',
  warehouse_receipt: 'bg-indigo-100 text-indigo-700',
  invoice: 'bg-emerald-100 text-emerald-700',
  weekly_summary: 'bg-cyan-100 text-cyan-700',
  payment_verified: 'bg-green-100 text-green-800',
  payment_rejected: 'bg-red-100 text-red-700',
  general: 'bg-gray-100 text-gray-700',
};

const AdminEmailLogs = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('todos');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [search, setSearch] = useState('');

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '200');
      if (filterType !== 'todos') params.append('email_type', filterType);
      if (filterStatus !== 'todos') params.append('status', filterStatus);

      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/email-logs?${params}`, axiosConfig),
        axios.get(`${API}/admin/email-logs/stats`, axiosConfig),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching email logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filterType, filterStatus]);

  const filtered = search
    ? logs.filter(l =>
        l.to_email?.toLowerCase().includes(search.toLowerCase()) ||
        l.subject?.toLowerCase().includes(search.toLowerCase())
      )
    : logs;

  return (
    <div className="space-y-6" data-testid="admin-email-logs">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Total Enviados</p>
                  <p className="text-2xl font-bold text-gray-900" data-testid="stat-total">{stats.total}</p>
                </div>
                <Mail className="text-blue-500" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Exitosos</p>
                  <p className="text-2xl font-bold text-green-600">{stats.sent}</p>
                </div>
                <CheckCircle className="text-green-500" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Fallidos</p>
                  <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
                </div>
                <XCircle className="text-red-500" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-gray-500 uppercase mb-2">Por Tipo</p>
              <div className="space-y-1">
                {stats.by_type?.slice(0, 4).map(t => (
                  <div key={t.type} className="flex justify-between text-xs">
                    <span className={`px-2 py-0.5 rounded-full ${TYPE_COLORS[t.type] || 'bg-gray-100 text-gray-600'}`}>
                      {TYPE_LABELS[t.type] || t.type}
                    </span>
                    <span className="font-bold">{t.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por email o asunto..."
                  className="pl-9"
                  data-testid="email-search"
                />
              </div>
            </div>
            <div className="w-[180px]">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los tipos</SelectItem>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[150px]">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="sent">Enviados</SelectItem>
                  <SelectItem value="failed">Fallidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-1" data-testid="refresh-logs">
              <RefreshCw size={14} /> Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail size={20} className="text-blue-600" />
            Historial de Emails ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-400">Cargando...</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="mx-auto text-gray-300 mb-3" size={40} />
              <p className="text-gray-400">No hay emails registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left p-3 font-medium text-gray-600">Fecha</th>
                    <th className="text-left p-3 font-medium text-gray-600">Destinatario</th>
                    <th className="text-left p-3 font-medium text-gray-600">Asunto</th>
                    <th className="text-left p-3 font-medium text-gray-600">Tipo</th>
                    <th className="text-left p-3 font-medium text-gray-600">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((log, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="p-3 text-gray-500 text-xs whitespace-nowrap">
                        {log.sent_at ? new Date(log.sent_at).toLocaleString('es-VE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="p-3 font-mono text-xs">{log.to_email}</td>
                      <td className="p-3 text-xs max-w-[300px] truncate" title={log.subject}>{log.subject}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[log.email_type] || 'bg-gray-100 text-gray-600'}`}>
                          {TYPE_LABELS[log.email_type] || log.email_type || 'General'}
                        </span>
                      </td>
                      <td className="p-3">
                        {log.status === 'sent' ? (
                          <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                            <CheckCircle size={14} /> Enviado
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600 text-xs font-medium" title={log.error || ''}>
                            <XCircle size={14} /> Fallido
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmailLogs;
